import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, API_BASE_URL, apiClient } from '../config/api';
import { auth } from '../firebase/config';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatRole = (role) =>
  (role || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const isAgentRole     = (role) => (role || '').toUpperCase() === 'AGENT';
// Mirrors mobile isBrokerRole — all three broker types share the same verification and registration flow
const isBrokerRole    = (role) => ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'].includes((role || '').toUpperCase());
const isBrokerageRole = (role) => (role || '').toUpperCase() === 'BROKERAGE';
const isAgentOrBrokerRole = (role) => isAgentRole(role) || isBrokerRole(role);
// Transaction Coordinators get their own dedicated registration screen (with an
// optional verify-first Brokerage Information section) instead of the generic
// Register screen — mirrors the isBrokerageRole/isAgentRole/isBrokerRole pattern.
const isTcRole = (role) => (role || '').toUpperCase() === 'TRANSACTION_COORDINATOR';

// A single-use (or expired) invite can be consumed on one platform and then retried on
// the other — e.g. accepted via the mobile app, then the same link opened on web. Detect
// that class of backend error so we show a clear message instead of a raw server string.
const isStaleInviteError = (msg) => {
  const m = (msg || '').toLowerCase();
  return m.includes('already accepted') || m.includes('already used') ||
    m.includes('expired') || m.includes('no longer valid') || m.includes('invalid or has expired');
};
const STALE_INVITE_MESSAGE =
  'This invitation has already been used or has expired. Please ask the person who invited you to send a new one.';

// Reads the first present param among several accepted spellings — keeps legacy link
// formats resilient if a future link-generator (mobile or backend) picks a slightly
// different key name for the same field (mirrors the multi-spelling tolerance already
// built into RegisterPage/BrokerVerificationPage's inviteData handling).
const readAliasParam = (params, ...names) => {
  for (const name of names) {
    const value = params.get(name);
    if (value) return value;
  }
  return '';
};

/**
 * Wait for Firebase Auth to finish initialising (up to `timeoutMs`).
 * Returns the Firebase user if one is cached, or null.
 */
const waitForFirebaseUser = (timeoutMs = 3000) =>
  new Promise((resolve) => {
    if (auth.currentUser !== undefined) { resolve(auth.currentUser); return; }
    const timer = setTimeout(() => { unsubscribe(); resolve(null); }, timeoutMs);
    const unsubscribe = auth.onAuthStateChanged((user) => {
      clearTimeout(timer); unsubscribe(); resolve(user);
    });
  });

/**
 * Get the current Firebase ID token (resolves null if not signed in).
 */
const getFirebaseToken = async () => {
  try {
    const firebaseUser = await waitForFirebaseUser(3000);
    if (firebaseUser) return await firebaseUser.getIdToken(true);
  } catch { /* no session */ }
  return null;
};

/**
 * Check whether a user exists by email.
 * Uses a plain fetch to avoid the shared 401→logout interceptor.
 */
const checkUserByEmail = async (email) => {
  if (!email) return null;
  try {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const token = await getFirebaseToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch { /* proceed without token */ }

    const res = await fetch(`${API_BASE_URL}/api/v1/users/check-email`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
      signal: AbortSignal.timeout(10000),
    });

    if (!res.ok) return null;
    const json = await res.json();
    const raw = json?.data || json?.content || json;
    if (!raw) return null;

    const exists =
      raw.exists === true ||
      (raw.exists !== false && !!(raw.id || raw.userId || raw.email || raw.firstName));

    return {
      exists,
      roles: Array.isArray(raw.roles) ? raw.roles : [],
      userId: raw.id || raw.userId || raw.user?.id || '',
    };
  } catch (e) {
    console.log('[InvitePage] check-email failed:', e);
    return null;
  }
};

// ─── Component ───────────────────────────────────────────────────────────────

const InvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // 'loading' | 'decoding' | 'accepting' | 'success' | 'error' | 'modal'
  const [status, setStatus]           = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [inviteModal, setInviteModal]  = useState({
    visible: false, title: '', message: '', buttonLabel: 'Continue', onContinue: () => {},
  });

  const showInviteModal = useCallback((title, message, onContinue, buttonLabel = 'Continue to Login') => {
    setInviteModal({ visible: true, title, message, buttonLabel, onContinue });
    setStatus('modal');
  }, []);

  const dismissInviteModal = useCallback(() => {
    const fn = inviteModal.onContinue;
    setInviteModal(prev => ({ ...prev, visible: false }));
    fn();
  }, [inviteModal]);

  // ── Main routing ─────────────────────────────────────────────────────────
  useEffect(() => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const continueBrowser = searchParams.get('browser') === '1'; 
    if (isMobile && !isAuthenticated && !continueBrowser) {
      // Preserve the role-aware path when redirecting to the static landing:
      // /sp-invite → /sp-invite.html (vendor app install prompt),
      // /invite    → /invite.html    (main app install prompt).
      const landing = window.location.pathname.indexOf('/sp-invite') === 0
        ? '/sp-invite.html'
        : '/invite.html';
      window.location.replace(landing + window.location.search);
      return;
    }
 

    // Accept a couple of historical/alternate param names for the invitation key so a
    // future link-generator change doesn't silently fall through to the less-capable
    // role-based path below.
    const key  = searchParams.get('key') || searchParams.get('transactionInvitationKey') || searchParams.get('invitationKey');
    const role = searchParams.get('role');

    // `key` must be checked before `role`: if a link ever carries both (e.g. a legacy
    // role-based link upgraded to include a resolved invitation key), the key-based path
    // is authoritative and should win. Do not reorder this.
    if (key)  { handleKeyInvite(key);           return; }
    if (role) { handleRoleBasedInvite(role);     return; }
    // Nothing actionable in URL
    setErrorMessage('No invitation data found in this link.');
    setStatus('error');
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-detect invite type (mirrors mobile SplashScreen) ────────────────
  /**
   * Tries user-invite validation first (`/api/v1/invitations/validate/{key}`).
   * If that throws, the key belongs to a transaction invite.
   * Returns { isUserInvite, userValidation }
   */
  const detectInviteType = async (key) => {
    try {
      const res = await apiClient.get(API_ENDPOINTS.validateInvitation(key));
      const data = res.data?.data || res.data?.content || res.data;
      const isUserInvite = data && data.success !== false;
      return { isUserInvite, userValidation: isUserInvite ? data : null };
    } catch {
      // Any failure (HTTP or network) means we can't confirm this is a user invite.
      // Fall through to the transaction invite path, which has its own network error guard.
      return { isUserInvite: false, userValidation: null };
    }
  };

  // ── Key-based invite (no type param) ─────────────────────────────────────
  const handleKeyInvite = async (key) => {
    setStatus('decoding');
    try {
      const { isUserInvite, userValidation } = await detectInviteType(key);

      // A stale localStorage 'isAuthenticated' flag with no live Firebase session must
      // use the unauthenticated path — otherwise we'd call /accept with 'Bearer null',
      // get a 401, and the axios interceptor would navigate away before we can show a
      // meaningful message.  Only treat the user as authenticated when we can actually
      // obtain a token.
      const storedAsAuth = localStorage.getItem('isAuthenticated') === 'true';
      const token        = storedAsAuth ? await getFirebaseToken() : null;
      const isAuthenticated = !!token;

      if (isUserInvite) {
        if (isAuthenticated) {
          await handleAuthenticatedUserInvite(key, userValidation);
        } else {
          await handleUnauthenticatedUserInvite(key, userValidation);
        }
      } else {
        if (isAuthenticated) {
          await handleAuthenticatedTransactionInvite(key);
        } else {
          await handleUnauthenticatedTransactionInvite(key);
        }
      }
    } catch {
      setErrorMessage('Unable to connect to the server. Please check your connection and try again.');
      setStatus('error');
    }
  };

  // ── Authenticated + user invite ───────────────────────────────────────────
  const handleAuthenticatedUserInvite = async (key, validation) => {
    try {
      const invitedRole  = (validation?.invitedRole || '').toUpperCase();
      const storedUser   = JSON.parse(localStorage.getItem('user') || '{}');
      const userRoles    = (
        Array.isArray(storedUser.roles) ? storedUser.roles :
        storedUser.role ? [storedUser.role] : []
      ).map(r => r.toUpperCase());
      const hasRole = userRoles.includes(invitedRole);

      if (hasRole) {
        // Already registered with this role — nothing to do (mirrors mobile)
        showInviteModal(
          'Already Registered',
          `You already have an account as ${formatRole(invitedRole)}. No action needed.`,
          () => navigate('/dashboard', { replace: true }),
          'Continue',
        );
        return;
      }

      // For Agent/Broker: accept + addRole now; verification happens on first role-switch
      setStatus('accepting');
      const token  = await getFirebaseToken();
      const userId = storedUser.id || storedUser.firebaseUid || '';

      // Accept the invitation
      await apiClient.post(
        API_ENDPOINTS.acceptUserInvite(key), {},
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      // Add role to user account (mirrors mobile authService.addRole).
      // Server enforces role-class exclusivity (SERVICE_PROVIDER vs main-app
      // roles) via 400 BadRequest — surface that reason instead of showing
      // a bogus success modal.
      if (userId) {
        try {
          await apiClient.post(
            API_ENDPOINTS.userRoles(userId),
            { role: invitedRole },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        } catch (roleErr) {
          console.error('[InvitePage] addRole failed:', roleErr);
          if (roleErr?.response?.status === 400) {
            const serverMsg = roleErr?.response?.data?.message
              || roleErr?.response?.data?.error
              || 'This role cannot be added to your account.';
            setErrorMessage(serverMsg);
            setStatus('error');
            return;
          }
          // Non-400 role-add errors are non-fatal: the invitation itself was
          // accepted above. Fall through so profile-refresh + success modal
          // still run; user can retry role-switching from the dashboard.
        }

        // Refresh profile in localStorage so Switch Role reflects the new role
        try {
          const profileRes = await apiClient.get(API_ENDPOINTS.userMe, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const updated = profileRes.data?.data || profileRes.data?.content || profileRes.data;
          if (updated && typeof updated === 'object') {
            localStorage.setItem('user', JSON.stringify({ ...storedUser, ...updated }));
          }
        } catch (pErr) {
          console.error('[InvitePage] profile refresh failed:', pErr);
        }
      }

      const existingRolesDisplay = userRoles.map(formatRole).join(', ');
      showInviteModal(
        'New Role Added',
        existingRolesDisplay
          ? `You were registered as ${existingRolesDisplay}. You've now been added as ${formatRole(invitedRole)}.`
          : `You've been added as ${formatRole(invitedRole)}.`,
        () => navigate('/dashboard', { replace: true }),
        'Continue',
      );
    } catch (error) {
      console.error('[InvitePage] Error accepting user invite:', error);
      const rawMsg = error.response?.data?.message || error.response?.data?.error || error.message || '';
      if (!error.response) {
        setErrorMessage('Unable to connect to the server. Please check your connection and try again.');
      } else if (isStaleInviteError(rawMsg)) {
        // Cross-platform race: this single-use invite was already accepted elsewhere
        // (e.g. via the mobile app) before this tab got to it.
        setErrorMessage(STALE_INVITE_MESSAGE);
      } else {
        setErrorMessage(rawMsg || 'Failed to accept invitation.');
      }
      setStatus('error');
    }
  };

  // ── Authenticated + transaction invite ────────────────────────────────────
  const handleAuthenticatedTransactionInvite = async (key) => {
    setStatus('accepting');
    try {
      const token      = await getFirebaseToken();
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const userId     = storedUser.id || storedUser.odooId || storedUser.firebaseUid || '';
      const userName   = [storedUser.firstName, storedUser.lastName].filter(Boolean).join(' ') || storedUser.fullName || '';

      const res = await apiClient.post(
        API_ENDPOINTS.acceptInvite,
        { invitationKey: key, userId, userName },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const txnId = res.data?.transactionId || res.data?.data?.transactionId;

      setStatus('success');
      sessionStorage.removeItem('inviteData');
      setTimeout(() => navigate(txnId ? `/transaction/${txnId}` : '/transactions', { replace: true }), 2000);
    } catch (error) {
      console.error('[InvitePage] Error accepting transaction invite:', error);
      const rawMsg = error.response?.data?.message || error.response?.data?.error || error.message || '';
      if (!error.response) {
        setErrorMessage('Unable to connect to the server. Please check your connection and try again.');
      } else if (isStaleInviteError(rawMsg)) {
        setErrorMessage(STALE_INVITE_MESSAGE);
      } else {
        setErrorMessage(rawMsg || 'Failed to accept invitation.');
      }
      setStatus('error');
    }
  };

  // ── Unauthenticated + user invite ─────────────────────────────────────────
  const handleUnauthenticatedUserInvite = async (key, validation) => {
    try {
      const invitedRole    = (validation?.invitedRole || '').toUpperCase();
      const inviteeEmail   = validation?.inviteeEmail || validation?.email || '';
      const inviteeName    = validation?.inviteeName  || validation?.name  || '';
      const nameParts      = inviteeName.trim().split(/\s+/);

      const inviteData = {
        key,
        type: 'user',  // resolved type for LoginPage
        role: invitedRole.toLowerCase(),
        email: inviteeEmail,
        phone: validation?.inviteePhone || validation?.phone || '',
        firstName: nameParts[0] || '',
        lastName: nameParts.length > 1 ? nameParts.slice(1).join(' ') : '',
        state: validation?.inviter?.state || validation?.state || '',
        brokerageName: validation?.inviter?.brokerageName || '',
        brokerLicenseNumber: validation?.inviter?.brokerLicenseNumber || '',
        brokerageAddress: validation?.inviter?.brokerageAddress || '',
        brokeragePhone: validation?.inviter?.brokeragePhone || '',
        brokerOfRecordName: validation?.inviter?.brokerOfRecordName || '',
        // Brokerage context fields (present on BROKERAGE invites and BROKER designated-broker
        // invites). Falls back to validation.inviter.* in case the backend ever nests these
        // under the inviter object instead, same as state/brokerageName below.
        companyName: validation?.companyName || validation?.inviter?.companyName || '',
        brokerageLicenseId: validation?.brokerageLicenseId || validation?.brokerageLicenceId ||
          validation?.inviter?.brokerageLicenseId || validation?.inviter?.brokerageLicenceId || '',
        isDesignatedBrokerInvite: validation?.isDesignatedBrokerInvite || false,
        // Vendor/Service Provider — the catalog id chosen on Invite Someone (RegisterPage
        // resolves this id to its display name before pre-selecting the picker).
        serviceType: validation?.serviceType || validation?.serviceTypeId || '',
        // Vendor Type (Individual/Company) — Company invites carry businessName
        // instead of a person name; RegisterPage must prefer this over the
        // firstName/lastName split above, which would otherwise mangle a
        // business name like "Acme Inspections LLC" into first/last name parts.
        vendorType: validation?.vendorType || 'INDIVIDUAL',
        businessName: validation?.businessName || '',
        // Inviting-company display context (only populated when the inviter is
        // a COMPANY-vendor SP). Pure display-affordance — RegisterPage renders
        // "Invited by [Company]"; it does NOT auto-attach the invitee to the
        // company.
        inviterCompanyName: validation?.inviter?.inviterCompanyName || '',
        inviterCompanyLogoUrl: validation?.inviter?.inviterCompanyLogoUrl || '',
      };
      sessionStorage.setItem('inviteData', JSON.stringify(inviteData));

      // Check if this email already has an account
      let existingUser = null;
      try {
        if (inviteeEmail) existingUser = await checkUserByEmail(inviteeEmail);
      } catch (e) {
        console.log('[InvitePage] check-email failed:', e);
      }

      if (existingUser?.exists && existingUser.roles?.length > 0) {
        const existingRoles   = existingUser.roles.map(formatRole).join(', ');
        const invitedDisplay  = formatRole(invitedRole);
        const hasRole         = existingUser.roles.some(r => r.toUpperCase() === invitedRole);

        if (!hasRole) {
          sessionStorage.setItem('pendingInviteRole', invitedRole);
        }

        showInviteModal(
          hasRole ? 'Account Exists' : 'New Role Invitation',
          hasRole
            ? `You already have an account as ${existingRoles}. Please log in to continue.`
            : `You're registered as ${existingRoles}. You've been invited as ${invitedDisplay}. Log in to accept.`,
          async () => {
            const token = await getFirebaseToken();
            if (token) {
              // User is already logged in — accept invite + refresh profile directly
              setStatus('accepting');
              try {
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const userId = storedUser.id || storedUser.firebaseUid || '';
                await apiClient.post(
                  API_ENDPOINTS.acceptUserInvite(key), {},
                  { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
                );
                if (userId) {
                  if (!hasRole) {
                    try {
                      await apiClient.post(
                        API_ENDPOINTS.userRoles(userId),
                        { role: invitedRole },
                        { headers: { 'Authorization': `Bearer ${token}` } }
                      );
                    } catch (roleErr) {
                      console.error('[InvitePage] addRole failed:', roleErr);
                    }
                  }
                  // Refresh profile via userMe so Switch Role reflects the new role immediately
                  try {
                    const profileRes = await apiClient.get(API_ENDPOINTS.userMe, {
                      headers: { 'Authorization': `Bearer ${token}` },
                    });
                    const updated = profileRes.data?.data || profileRes.data?.content || profileRes.data;
                    if (updated && typeof updated === 'object') {
                      localStorage.setItem('user', JSON.stringify({ ...storedUser, ...updated }));
                    }
                  } catch { /* non-critical */ }
                }
                sessionStorage.removeItem('inviteData');
                sessionStorage.removeItem('pendingInviteRole');
                navigate('/dashboard', { replace: true });
              } catch (err) {
                console.error('[InvitePage] Error accepting invite while logged in:', err);
                navigate('/dashboard', { replace: true });
              }
            } else {
              // Not logged in — navigate to login page (existing behavior)
              navigate('/login', {
                replace: true,
                state: { prefillEmail: inviteeEmail, pendingInviteAccept: true },
              });
            }
          },
        );
        return;
      }

      // New user — route to verification, brokerage registration, or standard register
      if (isBrokerageRole(invitedRole)) {
        navigate('/brokerage-registration', { replace: true, state: { inviteData } });
      } else if (isAgentRole(invitedRole)) {
        navigate('/agent-verification', { replace: true, state: { inviteData } });
      } else if (isBrokerRole(invitedRole)) {
        navigate('/broker-verification', { replace: true, state: { inviteData } });
      } else if (isTcRole(invitedRole)) {
        navigate('/tc-registration', { replace: true, state: { inviteData } });
      } else {
        navigate('/register', { replace: true, state: { inviteData } });
      }
    } catch (error) {
      console.error('[InvitePage] Error handling unauthenticated user invite:', error);
      setErrorMessage('This invitation link is invalid or has expired.');
      setStatus('error');
    }
  };

  // ── Unauthenticated + transaction invite ──────────────────────────────────
  const handleUnauthenticatedTransactionInvite = async (key) => {
    try {
      setStatus('decoding');
      const res  = await apiClient.get(API_ENDPOINTS.decodeInvite(key));
      const data = res.data?.data || res.data?.content || res.data;

      if (!data) {
        setErrorMessage('This invitation link is invalid or has expired.');
        setStatus('error');
        return;
      }

      const rawRole = (data.invitedRole || data.role || data.clientType || '').toUpperCase();
      const roleMap = {
        BUYER: 'buyer', SELLER: 'seller',
        AGENT: 'agent', BUYERS_AGENT: 'agent', LISTING_AGENT: 'agent',
        BROKER: 'broker',
        TRANSACTION_COORDINATOR: 'transaction_coordinator',
      };
      const mappedRole = roleMap[rawRole] || rawRole.toLowerCase();

      let firstName = data.firstName || '';
      let lastName  = data.lastName  || '';
      const fullName = data.inviteeName || data.name || '';
      if (!firstName && fullName) {
        const p = fullName.trim().split(/\s+/);
        firstName = p[0] || '';
        lastName  = p.length > 1 ? p.slice(1).join(' ') : '';
      }

      const inviteData = {
        key,
        type: 'transaction',  // resolved type for LoginPage
        role: mappedRole,
        email: data.inviteeEmail || data.email || '',
        phone: data.inviteePhone || data.phone || data.phoneNumber || '',
        firstName, lastName,
        state: data.state || data.propertyState || '',
        city: data.city || data.propertyCity || '',
        zipCode: data.zipCode || data.propertyZip || data.zip || '',
        transactionId: data.transactionId || '',
        propertyAddress: data.propertyAddress || '',
        // Vendor/Service Provider — the catalog id chosen on Invite Someone (RegisterPage
        // resolves this id to its display name before pre-selecting the picker).
        serviceType: data.serviceType || data.serviceTypeId || '',
        // Vendor Type (Individual/Company) — see identical note in
        // handleUnauthenticatedUserInvite above.
        vendorType: data.vendorType || 'INDIVIDUAL',
        businessName: data.businessName || '',
      };
      sessionStorage.setItem('inviteData', JSON.stringify(inviteData));

      // Check if this email already has an account
      let existingUser = null;
      try {
        if (inviteData.email) existingUser = await checkUserByEmail(inviteData.email);
      } catch (e) {
        console.log('[InvitePage] check-email failed:', e);
      }

      if (existingUser?.exists && existingUser.roles?.length > 0) {
        const existingRoles  = existingUser.roles.map(formatRole).join(', ');
        const invitedDisplay = formatRole(mappedRole) || 'client';

        showInviteModal(
          'Account Exists',
          `You're registered as ${existingRoles}. Log in to join this transaction as ${invitedDisplay}.`,
          async () => {
            const token = await getFirebaseToken();
            if (token) {
              // User is already logged in — accept transaction invite + refresh profile directly
              setStatus('accepting');
              try {
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const userId = storedUser.id || storedUser.odooId || storedUser.firebaseUid || '';
                const userName = [storedUser.firstName, storedUser.lastName].filter(Boolean).join(' ') || storedUser.fullName || '';
                const res = await apiClient.post(
                  API_ENDPOINTS.acceptInvite,
                  { invitationKey: key, userId, userName },
                  { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
                );
                // Refresh profile via userMe so profile is up to date
                try {
                  const profileRes = await apiClient.get(API_ENDPOINTS.userMe, {
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  const updated = profileRes.data?.data || profileRes.data?.content || profileRes.data;
                  if (updated && typeof updated === 'object') {
                    localStorage.setItem('user', JSON.stringify({ ...storedUser, ...updated }));
                  }
                } catch { /* non-critical */ }
                sessionStorage.removeItem('inviteData');
                const txnId = res.data?.transactionId || res.data?.data?.transactionId || inviteData.transactionId;
                setStatus('success');
                setTimeout(() => navigate(txnId ? `/transaction/${txnId}` : '/dashboard', { replace: true }), 2000);
              } catch (err) {
                console.error('[InvitePage] Error accepting transaction invite while logged in:', err);
                const rawMsg = err.response?.data?.message || err.response?.data?.error || err.message || '';
                if (!err.response) {
                  setErrorMessage('Unable to connect to the server. Please check your connection and try again.');
                } else if (isStaleInviteError(rawMsg)) {
                  setErrorMessage(STALE_INVITE_MESSAGE);
                } else {
                  setErrorMessage(rawMsg || 'Failed to accept invitation.');
                }
                setStatus('error');
              }
            } else {
              // Not logged in — navigate to login page (existing behavior)
              navigate('/login', {
                replace: true,
                state: { prefillEmail: inviteData.email, pendingInviteAccept: true },
              });
            }
          },
        );
        return;
      }

      // New user — route based on invited role (mirrors mobile SplashScreen routing)
      if (isBrokerageRole(mappedRole)) {
        navigate('/brokerage-registration', { replace: true, state: { inviteData } });
      } else if (isAgentRole(mappedRole)) {
        navigate('/agent-verification', { replace: true, state: { inviteData } });
      } else if (isBrokerRole(mappedRole)) {
        navigate('/broker-verification', { replace: true, state: { inviteData } });
      } else if (isTcRole(mappedRole)) {
        navigate('/tc-registration', { replace: true, state: { inviteData } });
      } else {
        navigate('/register', { replace: true, state: { inviteData } });
      }
    } catch (error) {
      console.error('[InvitePage] Error handling transaction invite:', error);
      if (!error.response) {
        setErrorMessage('Unable to connect to the server. Please check your connection and try again.');
        setStatus('error');
        return;
      }
      if (error.response?.status === 404 || error.response?.status === 400) {
        setErrorMessage('This invitation link is invalid or has expired.');
        setStatus('error');
        return;
      }
      navigate('/register', { replace: true });
    }
  };

  // ── Role-based (Copy Link) invite — ?role=...&email=...&name=... ──────────
  const handleRoleBasedInvite = async (role) => {
    const rawName = searchParams.get('name') || '';
    const parts = rawName.trim().split(/\s+/);
    const inviteData = {
      role,
      firstName: parts[0] || '',
      lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
      email: searchParams.get('email') || '',
      phone: searchParams.get('phone') || '',
      transactionId: searchParams.get('transactionId') || '',
      // Brokerage/Vendor context — only present on Brokerage or Service Provider legacy
      // links; empty string for every other role, same as the fields above. Read under a
      // couple of aliases so this doesn't silently drop data if a link-generator uses a
      // slightly different param name than we expect.
      companyName: readAliasParam(searchParams, 'companyName', 'company'),
      brokerageLicenseId: readAliasParam(searchParams, 'brokerageLicenseId', 'brokerageLicenceId', 'licenseId'),
      serviceType: readAliasParam(searchParams, 'serviceType', 'service_type', 'vendorServiceType'),
      vendorType: readAliasParam(searchParams, 'vendorType', 'vendor_type') || 'INDIVIDUAL',
      businessName: readAliasParam(searchParams, 'businessName', 'business_name'),
    };

    if (isBrokerageRole(role)) {
      sessionStorage.setItem('inviteData', JSON.stringify(inviteData));
      navigate('/brokerage-registration', { replace: true, state: { inviteData } });
      return;
    }

    if (isTcRole(role)) {
      sessionStorage.setItem('inviteData', JSON.stringify(inviteData));
      navigate('/tc-registration', { replace: true, state: { inviteData } });
      return;
    }

    if (isAgentOrBrokerRole(role)) {
      if (localStorage.getItem('isAuthenticated') === 'true') {
        inviteData.alreadyRegistered = true;
        sessionStorage.setItem('inviteData', JSON.stringify(inviteData));
        navigate(isAgentRole(role) ? '/agent-verification' : '/broker-verification',
          { replace: true, state: { inviteData } });
        return;
      }

      const existingAgent = inviteData.email ? await checkUserByEmail(inviteData.email) : null;
      if (existingAgent?.exists && existingAgent.roles?.length > 0) {
        inviteData.alreadyRegistered = true;
        sessionStorage.setItem('inviteData', JSON.stringify(inviteData));

        const hasRole = existingAgent.roles.some(r => r.toUpperCase() === role.toUpperCase());
        if (!hasRole) sessionStorage.setItem('pendingInviteRole', role.toUpperCase());

        const existingRoles = existingAgent.roles.map(formatRole).join(', ');
        const verifyPath = isAgentRole(role) ? '/agent-verification' : '/broker-verification';

        showInviteModal(
          'Verification Required',
          `You're registered as ${existingRoles}. To accept this ${formatRole(role)} invitation, please verify your license first.`,
          () => navigate(verifyPath, { replace: true, state: { inviteData } }),
          'Continue to Verification',
        );
      } else {
        sessionStorage.setItem('inviteData', JSON.stringify(inviteData));
        navigate(isAgentRole(role) ? '/agent-verification' : '/broker-verification',
          { replace: true, state: { inviteData } });
      }
      return;
    }

    // Non-agent/broker role
    if (localStorage.getItem('isAuthenticated') === 'true') {
      sessionStorage.setItem('inviteData', JSON.stringify(inviteData));
      navigate('/register', { replace: true, state: { inviteData } });
      return;
    }

    const existing = inviteData.email ? await checkUserByEmail(inviteData.email) : null;
    if (existing?.exists && existing.roles?.length > 0) {
      sessionStorage.setItem('inviteData', JSON.stringify(inviteData));
      const hasRole = existing.roles.some(r => r.toUpperCase() === role.toUpperCase());
      if (!hasRole) sessionStorage.setItem('pendingInviteRole', role.toUpperCase());

      const existingRoles = existing.roles.map(formatRole).join(', ');
      showInviteModal(
        hasRole ? 'Account Exists' : 'New Role Invitation',
        hasRole
          ? `You already have an account as ${existingRoles}. Please log in to continue.`
          : `You're registered as ${existingRoles}. You've been invited as ${formatRole(role)}. Log in to accept.`,
        () => navigate('/login', {
          replace: true,
          state: { prefillEmail: inviteData.email, pendingInviteAccept: true },
        }),
      );
    } else {
      sessionStorage.setItem('inviteData', JSON.stringify(inviteData));
      navigate('/register', { replace: true, state: { inviteData } });
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2D1B69 0%, #1a0f3f 100%)',
      color: 'white', fontFamily: 'Poppins, sans-serif',
    }}>
      <div style={{ textAlign: 'center', maxWidth: '440px', padding: '0 24px', width: '100%' }}>

        {(status === 'loading' || status === 'decoding') && (
          <>
            <div style={{
              width: '48px', height: '48px',
              border: '4px solid rgba(255,255,255,0.3)', borderTopColor: 'white',
              borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px',
            }} />
            <h2 style={{ margin: '0 0 8px', fontSize: '24px' }}>Processing Invitation...</h2>
            <p style={{ margin: 0, opacity: 0.8 }}>Please wait while we verify your invitation.</p>
          </>
        )}

        {status === 'accepting' && (
          <>
            <div style={{
              width: '48px', height: '48px',
              border: '4px solid rgba(255,255,255,0.3)', borderTopColor: 'white',
              borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px',
            }} />
            <h2 style={{ margin: '0 0 8px', fontSize: '24px' }}>Accepting Invitation...</h2>
            <p style={{ margin: 0, opacity: 0.8 }}>Please wait while we add you to the transaction.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '24px' }}>Invitation Accepted!</h2>
            <p style={{ margin: 0, opacity: 0.8 }}>You have been added successfully. Redirecting...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10007;</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '24px' }}>Invitation Failed</h2>
            <p style={{ margin: '0 0 24px', opacity: 0.8 }}>{errorMessage}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => window.location.reload()} style={{
                padding: '12px 24px', background: '#5B47D6', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
              }}>Try Again</button>
              <button onClick={() => navigate('/login', { replace: true })} style={{
                padding: '12px 24px', background: 'rgba(255,255,255,0.15)', color: 'white',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
                fontSize: '15px', fontWeight: '600', cursor: 'pointer',
              }}>Go to Login</button>
            </div>
          </>
        )}

      </div>

      {/* ─── Invite Modal ──────────────────────────────────────────────────── */}
      {inviteModal.visible && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '24px',
          animation: 'fadeIn 0.2s ease',
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '24px',
            paddingTop: '48px', paddingBottom: '36px',
            paddingLeft: '32px', paddingRight: '32px',
            maxWidth: '340px', width: '100%', textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            animation: 'slideUp 0.3s ease',
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #2D1B69 0%, #5B47D6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 4px 16px rgba(45,27,105,0.4)',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
                <path d="M16 11l2 2 4-4" />
              </svg>
            </div>

            <h2 style={{
              margin: '0 0 12px', fontSize: '20px', fontWeight: '700',
              color: '#1a1a2e', lineHeight: '1.3', fontFamily: 'Poppins, sans-serif',
            }}>
              {inviteModal.title}
            </h2>

            <p style={{
              margin: '0 0 28px', fontSize: '14px', color: '#666',
              lineHeight: '1.65', fontFamily: 'Poppins, sans-serif',
            }}>
              {inviteModal.message}
            </p>

            <button
              onClick={dismissInviteModal}
              style={{
                width: '100%', height: '52px',
                background: 'linear-gradient(to right, #2D1B69, #5B47D6)',
                color: 'white', border: 'none', borderRadius: '28px',
                fontSize: '15px', fontWeight: '600', cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif',
                boxShadow: '0 4px 16px rgba(91,71,214,0.4)',
                letterSpacing: '0.02em',
              }}
            >
              {inviteModal.buttonLabel}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default InvitePage;
